/**
 * hooks/useClientMatching.ts
 *
 * Manages the client matching resolution workflow.
 * Loads requests that need pro intervention (pending_choice, suggested)
 * and provides actions to confirm or create new clients.
 * Created Apr 2026.
 * Fix Apr 2026: emails are normalized (trim + lowercase) before comparison;
 *   partial-match branch is guarded against malformed emails without '@' and
 *   uses startsWith (anchored) instead of includes to reduce false positives.
 * Fix Apr 2026: dismiss now calls dismiss_client_match RPC (Option A) so dismissals
 *   persist across sessions/devices instead of being UI-only.
 * Updated Apr 2026: findCandidates now fetches client_type, company_name,
 *   company_registration_number to support adaptive display in the resolver modal.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { captureError } from '../utils/errorReporting';
import type { PendingMatchRequest, ClientCandidate } from '../types/merge';

interface UseClientMatchingParams {
  userId: string | undefined;
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

export function useClientMatching({ userId, showToast }: UseClientMatchingParams) {
  const [pendingRequests, setPendingRequests] = useState<PendingMatchRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const fetchPendingRequests = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('requests')
        .select('id, title, respondent_email, client_match_status, matched_at, created_at')
        .eq('user_id', userId)
        .in('client_match_status', ['pending_choice', 'suggested'])
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPendingRequests((data as PendingMatchRequest[]) || []);
    } catch (err) {
      captureError(err, { hook: 'useClientMatching', action: 'fetchPendingRequests' });
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchPendingRequests();
  }, [fetchPendingRequests]);

  const findCandidates = useCallback(
    async (respondentEmail: string | null): Promise<ClientCandidate[]> => {
      if (!userId) return [];
      try {
        const { data, error } = await supabase
          .from('clients')
          .select('id, client_type, first_name, last_name, name, email, phone, company_name, company_registration_number')
          .eq('user_id', userId)
          .is('deleted_at', null);

        if (error) throw error;

        const normalizedRespondent = respondentEmail?.trim().toLowerCase() ?? null;
        // Only derive a local-part for partial matching when the email is well-formed
        const localPart = normalizedRespondent?.includes('@')
          ? normalizedRespondent.split('@')[0]
          : null;

        return ((data as ClientCandidate[]) || [])
          .map((c) => {
            const reasons: string[] = [];
            let score = 0;

            if (normalizedRespondent && c.email) {
              const normalizedCandidate = c.email.trim().toLowerCase();
              if (normalizedCandidate === normalizedRespondent) {
                score += 80;
                reasons.push('Email identique');
              } else if (localPart && normalizedCandidate.startsWith(localPart)) {
                score += 20;
                reasons.push('Email similaire');
              }
            }

            return { ...c, confidence: score, matchReasons: reasons };
          })
          .filter((c) => c.confidence > 0)
          .sort((a, b) => b.confidence - a.confidence);
      } catch (err) {
        captureError(err, { hook: 'useClientMatching', action: 'findCandidates' });
        return [];
      }
    },
    [userId]
  );

  const confirmMatch = useCallback(
    async (requestId: string, clientId: string): Promise<boolean> => {
      setConfirming(true);
      try {
        const { error } = await supabase.rpc('confirm_client_match', {
          p_request_id: requestId,
          p_client_id: clientId,
          p_create_new: false,
        });
        if (error) throw error;
        showToast('Formulaire rattaché au client', 'success');
        setPendingRequests((prev) => prev.filter((r) => r.id !== requestId));
        return true;
      } catch (err) {
        captureError(err, { hook: 'useClientMatching', action: 'confirmMatch' });
        showToast('Erreur lors du rattachement', 'error');
        return false;
      } finally {
        setConfirming(false);
      }
    },
    [showToast]
  );

  const createNewClient = useCallback(
    async (requestId: string): Promise<boolean> => {
      setConfirming(true);
      try {
        const { error } = await supabase.rpc('confirm_client_match', {
          p_request_id: requestId,
          p_client_id: null,
          p_create_new: true,
        });
        if (error) throw error;
        showToast('Nouveau client créé', 'success');
        setPendingRequests((prev) => prev.filter((r) => r.id !== requestId));
        return true;
      } catch (err) {
        captureError(err, { hook: 'useClientMatching', action: 'createNewClient' });
        showToast('Erreur lors de la création du client', 'error');
        return false;
      } finally {
        setConfirming(false);
      }
    },
    [showToast]
  );

  const dismiss = useCallback(
    async (requestId: string): Promise<void> => {
      // Optimistic removal so the UI responds immediately
      setPendingRequests((prev) => prev.filter((r) => r.id !== requestId));
      try {
        const { error } = await supabase.rpc('dismiss_client_match', {
          p_request_id: requestId,
        });
        if (error) throw error;
      } catch (err) {
        captureError(err, { hook: 'useClientMatching', action: 'dismiss' });
        // Re-fetch to restore the dismissed request if the RPC failed
        fetchPendingRequests();
        showToast('Erreur lors de la suppression du rattachement', 'error');
      }
    },
    [fetchPendingRequests, showToast]
  );

  return {
    pendingRequests,
    loading,
    confirming,
    findCandidates,
    confirmMatch,
    createNewClient,
    dismiss,
    refresh: fetchPendingRequests,
  };
}
